import {
    GET_CHAT_USERS_REQUEST, GET_CHAT_USERS_SUCCESS, GET_CHAT_USERS_FAILURE,
    GET_CHAT_CONVERSATIONS_REQUEST, GET_CHAT_CONVERSATIONS_SUCCESS, GET_CHAT_CONVERSATIONS_FAILURE,
    START_DM_REQUEST, START_DM_SUCCESS, START_DM_FAILURE,
    CREATE_GROUP_REQUEST, CREATE_GROUP_SUCCESS, CREATE_GROUP_FAILURE,
    GET_CHAT_MESSAGES_REQUEST, GET_CHAT_MESSAGES_SUCCESS, GET_CHAT_MESSAGES_FAILURE,
    SEND_CHAT_MESSAGE_REQUEST, SEND_CHAT_MESSAGE_SUCCESS, SEND_CHAT_MESSAGE_FAILURE,
    DELETE_CHAT_MESSAGE_REQUEST, DELETE_CHAT_MESSAGE_SUCCESS, DELETE_CHAT_MESSAGE_FAILURE,
    UPLOAD_CHAT_FILE_REQUEST, UPLOAD_CHAT_FILE_SUCCESS, UPLOAD_CHAT_FILE_FAILURE,
    RECEIVE_SOCKET_MESSAGE, REMOVE_SOCKET_MESSAGE, CLEAR_CHAT_MESSAGES,
    ChatUser, ChatConversation, ChatMessage,
} from "../actions/chatAction";

interface ChatState {
    users: ChatUser[];
    usersLoading: boolean;
    usersError: string | null;

    conversations: ChatConversation[];
    conversationsLoading: boolean;
    conversationsError: string | null;

    messages: ChatMessage[];
    messagesLoading: boolean;
    messagesError: string | null;
    activeConversationId: string | null;
    messagesPage: number;
    hasMoreMessages: boolean;

    startDMLoading: boolean;
    startDMError: string | null;

    createGroupLoading: boolean;
    createGroupError: string | null;

    sendMessageLoading: boolean;
    sendMessageError: string | null;

    deleteMessageLoading: boolean;
    deleteMessageError: string | null;

    uploadLoading: boolean;
    uploadError: string | null;
    uploadedFileUrl: string | null;
}

const initialState: ChatState = {
    users: [], usersLoading: false, usersError: null,
    conversations: [], conversationsLoading: false, conversationsError: null,
    messages: [], messagesLoading: false, messagesError: null, activeConversationId: null, messagesPage: 1, hasMoreMessages: true,
    startDMLoading: false, startDMError: null,
    createGroupLoading: false, createGroupError: null,
    sendMessageLoading: false, sendMessageError: null,
    deleteMessageLoading: false, deleteMessageError: null,
    uploadLoading: false, uploadError: null, uploadedFileUrl: null,
};

const chatReducer = (state = initialState, action: any): ChatState => {
    switch (action.type) {
        case GET_CHAT_USERS_REQUEST:
            return { ...state, usersLoading: true, usersError: null };
        case GET_CHAT_USERS_SUCCESS:
            return { ...state, usersLoading: false, users: action.payload };
        case GET_CHAT_USERS_FAILURE:
            return { ...state, usersLoading: false, usersError: action.payload };

        case GET_CHAT_CONVERSATIONS_REQUEST:
            return { ...state, conversationsLoading: true, conversationsError: null };
        case GET_CHAT_CONVERSATIONS_SUCCESS:
            return { ...state, conversationsLoading: false, conversations: action.payload };
        case GET_CHAT_CONVERSATIONS_FAILURE:
            return { ...state, conversationsLoading: false, conversationsError: action.payload };

        case START_DM_REQUEST:
            return { ...state, startDMLoading: true, startDMError: null };
        case START_DM_SUCCESS: {
            const exists = state.conversations.some((c) => c._id === action.payload._id);
            return {
                ...state,
                startDMLoading: false,
                conversations: exists ? state.conversations : [action.payload, ...state.conversations],
            };
        }
        case START_DM_FAILURE:
            return { ...state, startDMLoading: false, startDMError: action.payload };

        case CREATE_GROUP_REQUEST:
            return { ...state, createGroupLoading: true, createGroupError: null };
        case CREATE_GROUP_SUCCESS:
            return { ...state, createGroupLoading: false, conversations: [action.payload, ...state.conversations] };
        case CREATE_GROUP_FAILURE:
            return { ...state, createGroupLoading: false, createGroupError: action.payload };

        case GET_CHAT_MESSAGES_REQUEST: {
            const isFresh =
                action.payload.conversationId !== state.activeConversationId || action.payload.page === 1;
            return {
                ...state,
                messagesLoading: true,
                messagesError: null,
                messages: isFresh ? [] : state.messages,
                activeConversationId: action.payload.conversationId,
                hasMoreMessages: isFresh ? true : state.hasMoreMessages,
                // opening a conversation clears its unread pill
                conversations: state.conversations.map((c) =>
                    c._id === action.payload.conversationId && c.unreadCount
                        ? { ...c, unreadCount: 0 }
                        : c
                ),
            };
        }
        case GET_CHAT_MESSAGES_SUCCESS: {
            const incoming = action.payload.messages || [];
            const limit = action.payload.limit ?? 30;
            return {
                ...state,
                messagesLoading: false,
                messages:
                    action.payload.page === 1
                        ? incoming
                        : [...incoming, ...state.messages],
                messagesPage: action.payload.page,
                hasMoreMessages: incoming.length >= limit,
            };
        }
        case GET_CHAT_MESSAGES_FAILURE:
            return { ...state, messagesLoading: false, messagesError: action.payload };

        case SEND_CHAT_MESSAGE_REQUEST:
            return { ...state, sendMessageLoading: true, sendMessageError: null };
        case SEND_CHAT_MESSAGE_SUCCESS:
            return {
                ...state,
                sendMessageLoading: false,
                messages: state.messages.some((m) => m._id === action.payload._id)
                    ? state.messages
                    : [...state.messages, action.payload],
            };
        case SEND_CHAT_MESSAGE_FAILURE:
            return { ...state, sendMessageLoading: false, sendMessageError: action.payload };

        case DELETE_CHAT_MESSAGE_REQUEST:
            return { ...state, deleteMessageLoading: true, deleteMessageError: null };
        case DELETE_CHAT_MESSAGE_SUCCESS:
        case REMOVE_SOCKET_MESSAGE:
            return {
                ...state,
                deleteMessageLoading: false,
                messages: state.messages.filter((m) => m._id !== action.payload),
            };
        case DELETE_CHAT_MESSAGE_FAILURE:
            return { ...state, deleteMessageLoading: false, deleteMessageError: action.payload };

        case UPLOAD_CHAT_FILE_REQUEST:
            return { ...state, uploadLoading: true, uploadError: null, uploadedFileUrl: null };
        case UPLOAD_CHAT_FILE_SUCCESS:
            return { ...state, uploadLoading: false, uploadedFileUrl: action.payload };
        case UPLOAD_CHAT_FILE_FAILURE:
            return { ...state, uploadLoading: false, uploadError: action.payload };

        case RECEIVE_SOCKET_MESSAGE: {
            const { message, conversationId } = action.payload;
            const isActive = conversationId === state.activeConversationId;
            const alreadyExists = state.messages.some((m) => m._id === message._id);

            // refresh the conversation's preview/unread count and float it to the top
            const target = state.conversations.find((c) => c._id === conversationId);
            const conversations = target
                ? [
                    {
                        ...target,
                        lastMessage: message,
                        updatedAt: message.createdAt || target.updatedAt,
                        unreadCount: isActive ? 0 : (target.unreadCount || 0) + (alreadyExists ? 0 : 1),
                    },
                    ...state.conversations.filter((c) => c._id !== conversationId),
                ]
                : state.conversations;

            return {
                ...state,
                conversations,
                messages: isActive && !alreadyExists ? [...state.messages, message] : state.messages,
            };
        }

        case CLEAR_CHAT_MESSAGES:
            return { ...state, messages: [], activeConversationId: null, messagesPage: 1, hasMoreMessages: true };

        default:
            return state;
    }
};

export default chatReducer;